import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { androidWatchesFormReducer } from './androidWatchesFormReducer';
import {
	IAndroidWatchesMessagesData,
	TTypeOfModalWindow,
	AndroidWatchesFormContext,
	androidWatchesFormInitialState,
} from './androidWatchesFormContext';
import { AndroidWatchesFormActionsTypes } from './androidWatchesFormAction';
import {
	IGetRestaurantAndroidWatchesMessages,
	TAndroidWatchesImportantMessages,
	TAndroidWatchesInformationMessages,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from '../../../../../editRestaurantState/editRestaurantContext';
import { useFetchData } from 'hooks/useFetchData';
import { getRestaurantAndroidWatchesMessages } from 'backend/watches';

export const AndroidWatchesFormState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(androidWatchesFormReducer, androidWatchesFormInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { visibleForm, restaurantId } = useContext<IEditRestaurantContext>(EditRestaurantContext);
	const needAndroidWatchesMessagesRequest = restaurantId && visibleForm === 'androidWatchesForm';

	const { refetch, response, loading } = useFetchData<IGetRestaurantAndroidWatchesMessages, string>(
		needAndroidWatchesMessagesRequest ? getRestaurantAndroidWatchesMessages : null,
		restaurantId || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: AndroidWatchesFormActionsTypes.SET_MESSAGES,
				payload: {
					importantMessages: response.data.importantMessages,
					informationMessages: response.data.informationMessages,
				},
			});
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [restaurantId, visibleForm]);

	const setImportantMessages = useCallback((value: TAndroidWatchesImportantMessages[]) => {
		dispatch({
			type: AndroidWatchesFormActionsTypes.SET_IMPORTANT_MESSAGES,
			payload: {
				importantMessages: value,
			},
		});
	}, []);

	const setInformationMessages = useCallback((value: TAndroidWatchesInformationMessages[]) => {
		dispatch({
			type: AndroidWatchesFormActionsTypes.SET_INFORMATION_MESSAGES,
			payload: {
				informationMessages: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IAndroidWatchesMessagesData) => {
		dispatch({
			type: AndroidWatchesFormActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: AndroidWatchesFormActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<AndroidWatchesFormContext.Provider
			value={{
				setImportantMessages,
				setInformationMessages,
				setDataForSubmit,
				changeTypeOfModalWindow,
				importantMessages: state.importantMessages,
				informationMessages: state.informationMessages,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</AndroidWatchesFormContext.Provider>
	);
};
